import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { PagesPaginateModel } from "../../models/Paginate/PagesPaginate";
import { ToastSettings } from "../../utils/ToastSettings";
import { ChatsFeedbackAnalysisService } from "../../services/Chats/ChatsFeedbackAnalysisService";
import { ChatFeedbackAnalysisModel } from "../../models/Chat/ChatFeedbackAnalysisModel";

interface ContextProps {
  vote: ChatFeedbackAnalysisModel;
  setVote: Dispatch<SetStateAction<any>>;
  votes: ChatFeedbackAnalysisModel[];
  params: any;
  setParams: Dispatch<SetStateAction<any>>;
  pages: PagesPaginateModel;
  setPages: Dispatch<SetStateAction<PagesPaginateModel>>;
  isLoading: boolean;
  error: string;
  setError: Dispatch<SetStateAction<string>>;
  handleList: () => void;
  handleFetch: (data: any) => Promise<boolean>;
}

export const ChatsFeedbackAnalysisContext = createContext<ContextProps>(
  {} as ContextProps
);

interface ChatsFeedbackAnalysisProviderProps {
  children: ReactNode;
}

export const ChatsFeedbackAnalysisProvider = ({
  children,
}: ChatsFeedbackAnalysisProviderProps) => {
  const [vote, setVote] = useState<ChatFeedbackAnalysisModel>(
    {} as ChatFeedbackAnalysisModel
  );
  const [votes, setVotes] = useState<ChatFeedbackAnalysisModel[]>([]);
  const [params, setParams] = useState<any>(null);
  const [pages, setPages] = useState<PagesPaginateModel>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");

  const service = new ChatsFeedbackAnalysisService();
  const location = useLocation();

  const handleList = useCallback(async () => {
    if (!params || !location.pathname.includes("/chats/messages/votes/list")) {
      return false;
    }

    try {
      setIsLoading(true);
      const [_Response, _Error] = await service.list(params);
      setIsLoading(false);

      if (!!_Error) {
        return setError(_Response?.message || _Error);
      }

      setVotes(_Response?.data?.items);
      setPages(_Response?.data?.pagination);
      return setError("");
    } catch (e) {
      setIsLoading(false);
      setError("Não foi possível carregar os votos do sistema.");
    }
  }, [params, location.pathname]);

  const handleFetch = async (data: any) => {
    try {
      const [_Response, _Error, _Code, _Errors] = await service.fetch(data);
      if (!!_Error) {
        ToastSettings(
          _Response?.message || _Error,
          "bottom-center",
          "error",
          () => {},
          _Errors
        );
        return false;
      }

      setError("");
      return true;
    } catch (e) {
      setError("Houve um erro ao salvar o exemplo");
      return false;
    }
  };

  useEffect(() => {
    !!error && toast.error(error);
  }, [error]);

  useEffect(() => {
    handleList().then();
  }, [handleList]);

  return (
    <ChatsFeedbackAnalysisContext.Provider
      value={{
        vote,
        setVote,
        votes,
        params,
        setParams,
        pages,
        setPages,
        isLoading,
        error,
        setError,
        handleList,
        handleFetch,
      }}
    >
      {children}
    </ChatsFeedbackAnalysisContext.Provider>
  );
};

export const useChatsFeedbackAnalysis = () =>
  useContext(ChatsFeedbackAnalysisContext);
